import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';

// Every read of the org-unit tree that goes through a form lookup (the parent/classification tree
// pickers) is fetched with `useCache: true` and never expires on its own, so a unit created or
// renamed here would not show up in a picker until a hard refresh. Both URL shapes are dropped:
// `manage/organizationalunit` (the tree picker's own lookup) and the `form-lookup/organizationalunit*`
// endpoints that resolve a single unit or the flat list.
function invalidateOrgUnitCache() {
  fetchApi.invalidateCache(url => url.includes('organizationalunit'));
}

export const OrganizationalUnitApi = {
  async searchOrganizationUnit({ offset, size, sortBy, ...filters }) {
    const query = { offset, size, sortBy };

    for (const [key, value] of Object.entries(filters)) {
      if (value == null) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      query[key] = value;
    }

    return checkErrors(await fetchApi.api.get('manage/organizationalunit', { query }));
  },

  async getOrganizationUnit({ id, includeAncestors = false }) {
    return checkErrors(await fetchApi.api.get(`manage/organizationalunit/${id}`, { query: { includeAncestors } }));
  },

  async getOrganizationUnitStub({ id }) {
    return (await checkErrors(await fetchApi.lookup.get(`organizationalunit/${id}`))).data;
  },

  async editOrganizationUnit({ id, ...data }) {
    const result = await checkErrors(await fetchApi.api.post(`manage/organizationalunit/${id}`, { data }));
    invalidateOrgUnitCache();

    return result;
  },

  // The caller's own org unit ("My Institution"). No id: the backend resolves it from the logged-in
  // user, which is what lets it serve callers who cannot open arbitrary units.
  async getMyOrganizationUnit() {
    return checkErrors(await fetchApi.api.get('manage/organizationalunit/mine'));
  },

  // Saves only the contact info of an org unit in the caller's scope, whatever else the form holds.
  async editOrganizationUnitContactInfo({ id, orgContactInfo }) {
    return checkErrors(await fetchApi.api.post(`manage/organizationalunit/${id}/contact-info`, { data: { orgContactInfo } }));
  },

  async createOrganizationUnit(data) {
    const result = await checkErrors(await fetchApi.api.post('manage/organizationalunit/', { data }));
    invalidateOrgUnitCache();

    return result;
  },
};

export default OrganizationalUnitApi;
